import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  email = '';
  password = '';
  loading = false;
  error: string | null = null;
  hidePassword = true;

  // Forgot-password inline flow
  showForgotPassword = false;
  forgotEmail = '';
  forgotLoading = false;
  forgotMessage: string | null = null;
  forgotError: string | null = null;

  constructor() {
    // If already logged in, redirect to admin
    if (this.authService.isAuthenticated()) {
      if (this.authService.isAdmin()) {
        this.router.navigate(['/admin']);
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  login() {
    if (!this.email || !this.password) {
      this.error = 'Please enter your email and password.';
      return;
    }

    this.loading = true;
    this.error = null;

    this.authService.login(this.email, this.password).subscribe({
      next: () => {
        this.loading = false;
        if (this.authService.isAuthenticated()) {
          if (this.authService.isAdmin()) {
            this.router.navigate(['/admin']);
          } else {
            this.router.navigate(['/']);
          }
        }
      },
      error: (error) => {
        this.loading = false;
        // Use a single generic message for every authentication failure so the
        // server never reveals whether an account exists or which field was
        // wrong (account-enumeration hardening). Only a genuine network error
        // (no HTTP response) gets a distinct hint.
        this.error =
          error.status === 0
            ? 'We could not reach the server. Please check your connection and try again.'
            : 'That email and password combination did not match. Please try again.';
      },
    });
  }

  toggleForgotPassword() {
    this.showForgotPassword = !this.showForgotPassword;
    this.forgotEmail = this.email;
    this.forgotMessage = null;
    this.forgotError = null;
  }

  sendForgotPassword() {
    if (!this.forgotEmail) {
      this.forgotError = 'Please enter your email address.';
      return;
    }

    this.forgotLoading = true;
    this.forgotMessage = null;
    this.forgotError = null;

    this.authService.forgotPassword(this.forgotEmail).subscribe({
      next: () => {
        this.forgotLoading = false;
        // Generic confirmation regardless of whether the account exists.
        this.forgotMessage =
          'If an account exists for that email, a password reset link is on its way.';
      },
      error: () => {
        this.forgotLoading = false;
        // Same generic message on error to avoid leaking account existence.
        this.forgotMessage =
          'If an account exists for that email, a password reset link is on its way.';
      },
    });
  }
}
